// ABOUTME: An agent's reply rendered as Markdown (paragraphs, lists, tables, code blocks with a
// copy button), shared by every chat that shows agent text; MarkdownText gives the same text
// with its formatting dropped, for a one-line preview.
import { lazy, memo, Suspense, useMemo, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const HighlightedCode = lazy(() => import('@/components/layout/HighlightedCode'))

// Code block component with copy functionality
function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    void navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="group/code relative my-2 overflow-hidden rounded-lg" data-testid="code-block">
      <div className="bg-ink-700 flex items-center justify-between px-3 py-1.5">
        <span className="text-paper-dim text-xs font-medium">{language}</span>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleCopy}
              aria-label={copied ? 'Code copied' : 'Copy code'}
              className="text-paper-dim hover:text-paper h-6 w-6"
              data-testid="copy-code-button"
            >
              {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">{copied ? 'Copied!' : 'Copy code'}</TooltipContent>
        </Tooltip>
      </div>
      <Suspense
        fallback={
          <pre className="bg-ink-800 text-paper overflow-x-auto p-3 text-[0.8125rem] leading-normal">
            <code>{code}</code>
          </pre>
        }
      >
        <HighlightedCode code={code} language={language} />
      </Suspense>
    </div>
  )
}

// Markdown-rendered message content
export const MarkdownContent = memo(function MarkdownContent({
  content,
  isStreaming,
}: {
  content: string
  isStreaming?: boolean
}) {
  const components = useMemo(
    () => ({
      // Fenced code blocks
      code({ className, children, ...props }: React.ComponentProps<'code'> & { node?: unknown }) {
        const match = /language-(\w+)/.exec(className || '')
        const codeStr = String(children).replace(/\n$/, '')

        // Inline code (no language class and short content)
        if (!match) {
          return (
            <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-[0.8125rem]" {...props}>
              {children}
            </code>
          )
        }

        // Fenced code block
        return <CodeBlock code={codeStr} language={match[1]} />
      },

      // Block-level elements
      p({ children }: React.ComponentProps<'p'>) {
        return <p className="mb-2 text-[15px] leading-relaxed last:mb-0">{children}</p>
      },
      h1({ children }: React.ComponentProps<'h1'>) {
        return <h1 className="mt-3 mb-2 text-base font-bold first:mt-0">{children}</h1>
      },
      h2({ children }: React.ComponentProps<'h2'>) {
        return <h2 className="mt-3 mb-2 text-sm font-bold first:mt-0">{children}</h2>
      },
      h3({ children }: React.ComponentProps<'h3'>) {
        return <h3 className="mt-2 mb-1.5 text-sm font-semibold first:mt-0">{children}</h3>
      },
      h4({ children }: React.ComponentProps<'h4'>) {
        return <h4 className="mt-2 mb-1 text-sm font-medium first:mt-0">{children}</h4>
      },
      ul({ children }: React.ComponentProps<'ul'>) {
        return <ul className="mb-2 ml-4 list-disc space-y-0.5 text-[15px] last:mb-0">{children}</ul>
      },
      ol({ children }: React.ComponentProps<'ol'>) {
        return (
          <ol className="mb-2 ml-4 list-decimal space-y-0.5 text-[15px] last:mb-0">{children}</ol>
        )
      },
      li({ children }: React.ComponentProps<'li'>) {
        return <li className="leading-relaxed">{children}</li>
      },
      blockquote({ children }: React.ComponentProps<'blockquote'>) {
        return (
          <blockquote className="border-primary/40 my-2 border-l-2 pl-3 text-sm italic opacity-90">
            {children}
          </blockquote>
        )
      },
      hr() {
        return <hr className="my-3 border-current opacity-20" />
      },
      a({ href, children }: React.ComponentProps<'a'>) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            {children}
          </a>
        )
      },
      strong({ children }: React.ComponentProps<'strong'>) {
        return <strong className="font-semibold">{children}</strong>
      },

      // Tables
      table({ children }: React.ComponentProps<'table'>) {
        return (
          <div className="my-2 overflow-x-auto rounded-md border">
            <table className="w-full text-sm">{children}</table>
          </div>
        )
      },
      thead({ children }: React.ComponentProps<'thead'>) {
        return <thead className="bg-muted/50 border-b">{children}</thead>
      },
      th({ children }: React.ComponentProps<'th'>) {
        return <th className="px-3 py-1.5 text-left text-xs font-semibold">{children}</th>
      },
      td({ children }: React.ComponentProps<'td'>) {
        return <td className="border-t px-3 py-1.5">{children}</td>
      },

      // Pre — let code handle rendering
      pre({ children }: React.ComponentProps<'pre'>) {
        return <>{children}</>
      },
    }),
    []
  )

  return (
    <div className="markdown-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
      {isStreaming && (
        <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse rounded-sm bg-current" />
      )}
    </div>
  )
})

/** Markdown as its words only: every element unwrapped to its text, for a one-line preview. */
export function MarkdownText({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} allowedElements={[]} unwrapDisallowed>
      {content}
    </ReactMarkdown>
  )
}
